import { ApolloQueryResult } from 'apollo-client';
import { get, pick, uniq } from 'lodash';

import { SaveAdsInput } from '../../api/banners/BannersMutationExecutor';
import { UploadImageResult } from '../../api/RestApiManager';
import { KeywordsTextTypeView } from '../../components/DisplayConditions/DisplayConditions';
import { SPECIAL_CREATIVE_GROUP_ID_FOR_GROUP_WITHOUT_ID } from '../../constants';
import {
    getBannerPriceData,
    getDefaultBannerPriceData,
    getDefaultVCardData,
    getMessengerTypeFromApi,
    getSiteLinksFromQueryResult,
    getTimeFromApi,
    mapAdImageFormatsFragmentToBannerImageFormat,
    mapUploadImageResultFormatToBannerImageFormat
} from '../../service/BannerHelpers/BannerHelpers';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { getUrlFromImageItem } from '../../service/ImageHelpers/ImageHelpers';
import {
    getAgeFromBannerModerationTheme,
    getWarningFromBannerModerationTheme
} from '../../service/Moderation/Moderation';
import { UrlHelpers } from '../../service/UrlHelpers';
import {
    BannerId,
    BannersAndImageEditData,
    BannersAndImageEditDataCopy,
    BannersBaseConstants,
    BannersEditApiData,
    BannersEditBaseInfoData,
    CalloutsConstants,
    CanvasConstants,
    Day,
    ImageItem,
    ImageOrCreativeUploadingMode,
    MetroStationInfo,
    PrevEditedBanner,
    SaveBannersResult,
    SitelinksConstants,
    VCardConstants,
    VcardData,
    WeekInfo
} from '../../typings/banners';
import {
    BannerVideoCreativeApiData,
    BannerVideoData,
    CreativeAndGroupsTypeEnum,
    CreativesAndGroupsList,
    EditedBanner,
    EditedBannerBase,
    EditedBannerCallouts,
    EditedBannerCreative,
    EditedBannerFullContacts,
    EditedBannerGroup,
    EditedBannerImage,
    EditedBannerPrice,
    EditedBannerSiteLinks,
    EditedBannerTurbolanding,
    EditedBannerTypeContentPromotionBase,
    EditedBannerTypeContentPromotionCollection,
    EditedBannerTypeContentPromotionVideo,
    EditedBannerTypeCpcVideo,
    EditedBannerTypeCpmAudio,
    EditedBannerTypeCpmBanner,
    EditedBannerTypeCpmGeoproduct,
    EditedBannerTypeCpmIndoor,
    EditedBannerTypeCpmOutdoor,
    EditedBannerTypeCpmVideo,
    EditedBannerTypeDynamic,
    EditedBannerTypeImage,
    EditedBannerTypeMcBanner,
    EditedBannerTypeMobileContent,
    EditedBannerTypePerformance,
    EditedBannerTypeText,
    EditedBannerVCard,
    EditedBannerVideo,
    getEditedBannerWithFullContacts,
    GroupBanners,
    MapPerformanceCreatives,
    Measurers,
    PerformanceCreativeGroups,
    PerformanceCreatives,
    PerformanceSelectedGroupCreatives,
    PixelsApiFormat,
    PromocodesRestrictedDomains,
    ProtocolValue,
    TurboGalleryDataEditor
} from '../../typings/bannersEditState';
import { MeasurerSystem, OperatorModerationInfo } from '../../typings/common';
import { CreativeType } from '../../typings/creative';
import {
    AdvType,
    AdvTypeClient,
    BannerImageForEdit,
    BannerImageType,
    BannerModerationTheme,
    CampaignId,
    ContactsEditorType,
    CopyTooltipStatus
} from '../../typings/domain';
import {
    adConstantsFragment,
    AddAds,
    AddContentPromotionAds,
    AddCpmAds,
    AddMcBannerAds,
    AddSmartAds,
    adsRowFragmentForEdit,
    adsRowFragmentForEdit_GdCPMBannerAd,
    adsRowFragmentForEdit_GdCPMBannerAd_measurers,
    adsRowFragmentForEdit_GdCPMBannerAd_pixels,
    adsRowFragmentForEdit_GdTextAd,
    adsRowFragmentForEdit_GdTextAd_adGroup_GdDynamicAdGroup_campaign,
    BannersQueryForEdit,
    BannersQueryForEdit_callouts,
    BannersQueryForEdit_client_campaigns_rowset,
    BaseInfoForBannerCreationQuery,
    BaseInfoForBannerCreationQuery_client_campaigns_rowset,
    BaseInfoForBannerCreationQuery_client_campaigns_rowset_GdTextCampaign_commonVcard,
    calloutsConstantsFragment,
    CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_commonVcard,
    configurationFragment,
    CreativesQuery,
    CreativesQuery_creatives_rowset,
    CurrencyCode,
    DeleteAdsWithValidation,
    GdAdImageNamespace,
    GdAdPermalinkAssignType,
    GdAdPrimaryStatus,
    GdAdType,
    GdCreativeType,
    GdPixelKind,
    GdVideoGoalType,
    GdVideoGoalTypeInput,
    GenerateVideoAddition_generateVideoAddition,
    GetLastChangedAds,
    GetLastChangedAds_client_lastChangedAds_lastChangedAds,
    ImagesQueryForEdit,
    ImagesQueryForEdit_images_rowset,
    MetroStationsQuery,
    MetroStationsQuery_constants_metroStations,
    PerformanceCreativesQuery,
    PerformanceCreativesQuery_client_adGroups_rowset_GdSmartAdGroup_availableCreatives_rowset,
    PerformanceCreativesQuery_client_ads_rowset_GdSmartAd,
    RecommendationsAdsGroupEditQuery,
    RecommendationsAdsGroupEditQuery_client_adGroups_rowset,
    sitelinkConstantsFragment,
    UpdateAds,
    UpdateContentPromotionAds,
    UpdateCpmAds,
    UpdateMcBannerAds,
    UpdateSmartAds,
    ValidationError,
    vcardConstantsFragment,
    VCardsForBannersEdit,
    VCardsForBannersEdit_vcards_rowset,
    VCardsForBannersEdit_vcards_rowset_workTimes
} from '../../typings/graphql-grid';
import { AudienceCollection } from '../../typings/groupsEditState';
import { EditPageRecommendationsData } from '../../typings/recommendations';
import { ImageEditorState } from '../../typings/state';
import { Omit } from '../../typings/utils';
import { VideoState } from '../../typings/video';
import { mapStringToAdvType } from '../banner/banner';
import {
    getValueFromApiFormat,
    mapCampaignRowPlatformToCampaignPlatform,
    mapCampaignRowTypeToCampaignType,
    mapGroupRowTypeToGroupType
} from '../common/common';
import { isRecommendationSupportedOnEditBannerPage, mapBaseRecommendationInfoFromApiFormat } from '../recommendation';

export function getBannerImageFromApiHashFormat(hash: string, uid: string): string {
    return `/storage/${uid}/banner_images_uploads/${hash}`;
}

export function getEditedBannerImage(row: adsRowFragmentForEdit): EditedBannerImage {
    const creative = row.typedCreative || null;
    const image = row.image || row.bannerImage || null;
    let imageData;

    if (creative !== null && creative.creativeId !== null && creative.creativeType === GdCreativeType.CANVAS) {
        imageData = {
            height: Number(creative.height),
            width: Number(creative.width),
            name: creative.name,
            url: creative.previewUrl,
            type: BannerImageType.CREATIVE,
            id: creative.creativeId,
            isAdaptive: (creative.__typename === 'GdCanvasCreative' && creative.isAdaptive) || false
        };
    } else if (!image || !image.name || !image.imageSize.height || !image.imageSize.width) {
        imageData = undefined;
    } else if (image.mdsGroupId) {
        imageData = {
            height: Number(image.imageSize.height),
            width: Number(image.imageSize.width),
            name: image.name,
            url: getUrlFromImageItem(image.mdsGroupId, image.namespace, image.imageHash),
            hash: image.imageHash,
            type: row && row.image ? BannerImageType.BANNER : BannerImageType.TEXT,
            formats: image && image.formats ? mapAdImageFormatsFragmentToBannerImageFormat(image.formats) : undefined
        };
    }

    return {
        mode: imageData ? ImageOrCreativeUploadingMode.UPLOADED : ImageOrCreativeUploadingMode.EMPTY,
        displayName: imageData ? imageData.name : '',
        imageUrl: imageData ? imageData.url : '',
        uploadProgress: undefined,
        data: imageData,
        fixedSize:
            imageData &&
            row &&
            (row.type === GdAdType.IMAGE_AD || row.type === GdAdType.MCBANNER || row.type === GdAdType.CPM_BANNER)
                ? {
                      height: Number(imageData.height),
                      width: Number(imageData.width),
                      isAdaptive:
                          (creative && creative.__typename === 'GdCanvasCreative' && creative.isAdaptive) || false
                  }
                : undefined
    };
}

export function getEditedBannerVCard(
    vcardId?: string | null,
    campaignId?: string | null,
    isDefault: boolean = false
): EditedBannerVCard {
    return {
        isShortVersionChecked: false,
        isNotShowMetroInSearchChecked: false,
        info: (!vcardId && campaignId) || isDefault ? getDefaultVCardData() : undefined
    };
}

export function getEditedBannerSiteLinks(row?: adsRowFragmentForEdit): EditedBannerSiteLinks {
    return {
        isOpen: false,
        items: row && row.sitelinks ? getSiteLinksFromQueryResult(row.sitelinks) : getSiteLinksFromQueryResult()
    };
}

export function getEditedBannerCallouts(row?: adsRowFragmentForEdit): EditedBannerCallouts {
    return {
        selected: ((row && row.callouts) || []).map(callout => callout.id.toString()),
        isEditorOpen: false
    };
}

export function getEditedBannerTurboGallery(row?: adsRowFragmentForEdit): TurboGalleryDataEditor {
    return {
        href: (row && 'turboGalleryHref' in row && row.turboGalleryHref) || ''
    };
}

export function getTextBannerImageFromUploadResponse(
    response: UploadImageResult,
    type: BannerImageType
): BannerImageForEdit & Required<Pick<BannerImageForEdit, 'hash'>> {
    return {
        height: Number(response.height),
        width: Number(response.width),
        name: response.name,
        url: getUrlFromImageItem(
            String(response.mds_group_id),
            response.mds_namespace as GdAdImageNamespace,
            response.hash
        ),
        hash: response.hash,
        formats: mapUploadImageResultFormatToBannerImageFormat(response.formats),
        type
    };
}

export function getBannerContactsData(banner: EditedBanner): EditedBannerFullContacts {
    const bannerWithContacts = getEditedBannerWithFullContacts(banner);
    const isCpaPayForConversionEnabled = true;

    if (!bannerWithContacts) {
        throw 'Contacts not supported for banner type ' + banner.bannerType;
    }

    return {
        ...bannerWithContacts.contacts,
        desktopAndTabletType: isCpaPayForConversionEnabled ? ContactsEditorType.TURBO : ContactsEditorType.LINK,
        mobileType: isCpaPayForConversionEnabled ? ContactsEditorType.TURBO : ContactsEditorType.LINK
    };
}

export function getEditedBannerVideo(row?: adsRowFragmentForEdit): EditedBannerVideo {
    return {
        data: getEditedBannerDataVideo(row)
    };
}

export function getEditedBannerDataVideo(row?: adsRowFragmentForEdit): BannerVideoData | undefined {
    const creative = row && row.typedCreative;

    if (creative && creative.creativeId) {
        switch (creative.__typename) {
            case CreativeType.GdCpmVideoCreative:
            case CreativeType.GdVideoAdditionCreative:
            case CreativeType.GdCpcVideoCreative:
            case CreativeType.GdCpmIndoorCreative:
            case CreativeType.GdCpmOutdoorCreative:
                return {
                    id: creative.creativeId,
                    name: creative.name,
                    previewUrl: creative.previewUrl,
                    livePreviewUrl: 'livePreviewUrl' in creative ? creative.livePreviewUrl : undefined,
                    formats: 'additionalData' in creative ? creative.additionalData.formats : [],
                    duration: 'additionalData' in creative ? creative.additionalData.duration || undefined : undefined
                };
            case CreativeType.GdCpmAudioCreative:
            case CreativeType.GdOverlayCreative:
                return {
                    id: creative.creativeId,
                    name: creative.name,
                    previewUrl: creative.previewUrl,
                    livePreviewUrl: 'livePreviewUrl' in creative ? creative.livePreviewUrl : undefined,
                    duration: 'duration' in creative ? creative.duration : undefined
                };
            default:
                return;
        }
    }

    return;
}

export function getEditedBannerCreative(row?: adsRowFragmentForEdit): EditedBannerCreative {
    const creative = row && row.typedCreative;
    let data;

    if (creative && creative.creativeId) {
        switch (creative.creativeType) {
            case GdCreativeType.HTML5_CREATIVE:
            case GdCreativeType.CANVAS:
                data = {
                    id: creative.creativeId,
                    name: creative.name,
                    previewUrl: creative.previewUrl,
                    width: Number(creative.width),
                    height: Number(creative.height),
                    isAdaptive: creative.__typename === 'GdCanvasCreative' ? Boolean(creative.isAdaptive) : false,
                    isImage: 'isImage' in creative ? creative.isImage : false,
                    type: creative.creativeType
                };
                break;
            default:
                data = undefined;
        }
    }

    return { data };
}

export function getPixelsFromApiFormat(banner: adsRowFragmentForEdit_GdCPMBannerAd): PixelsApiFormat {
    const pixels = getValueFromApiFormat(banner.pixels);
    const res: PixelsApiFormat = { audience: [], audit: [] };
    if (pixels) {
        return pixels.reduce((currentRes, pixel: adsRowFragmentForEdit_GdCPMBannerAd_pixels) => {
            if (pixel.kind === GdPixelKind.AUDIENCE) {
                currentRes.audience.push(pixel.url);
            } else if (pixel.kind === GdPixelKind.AUDIT) {
                currentRes.audit.push(pixel.url);
            }
            return currentRes;
        }, res);
    }
    return res;
}

export function getMeasurersFromApiFormat(banner: adsRowFragmentForEdit_GdCPMBannerAd): Measurers[] {
    const measurers = getValueFromApiFormat(banner.measurers) || [];
    return measurers.map((measure: adsRowFragmentForEdit_GdCPMBannerAd_measurers) => ({
        params: measure.params,
        measurerSystem: measure.measurerSystem,
        hasIntegration: Boolean(measure.hasIntegration)
    }));
}

interface BannersSaveApiResponseData {
    addAds: AddAds;
    updateAds: UpdateAds;
    addSmartAds: AddSmartAds;
    updateSmartAds: UpdateSmartAds;
    addCpmAds: AddCpmAds;
    updateCpmAds: UpdateCpmAds;
    addMcBannerAds: AddMcBannerAds;
    updateMcBannerAds: UpdateMcBannerAds;
    addContentPromotionAds: AddContentPromotionAds;
    updateContentPromotionAds: UpdateContentPromotionAds;
    deleteAds: DeleteAdsWithValidation;
}

export const mapBannersSaveApiResponse = (data: BannersSaveApiResponseData, input: SaveAdsInput): SaveBannersResult => {
    const {
        addAds,
        updateAds,
        addSmartAds,
        updateSmartAds,
        addCpmAds,
        updateCpmAds,
        addMcBannerAds,
        updateMcBannerAds,
        addContentPromotionAds,
        updateContentPromotionAds,
        deleteAds
    } = data;
    const updateErrors = get(updateAds, 'validationResult.errors', []);
    const addErrors = get(addAds, 'validationResult.errors', []);
    const addSmartErrors = get(addSmartAds, 'validationResult.errors', []);
    const updateSmartErrors = get(updateSmartAds, 'validationResult.errors', []);
    const addCpmErrors = get(addCpmAds, 'validationResult.errors', []);
    const updateCpmErrors = get(updateCpmAds, 'validationResult.errors', []);
    const addMcBannerErrors = get(addMcBannerAds, 'validationResult.errors', []);
    const updateMcBannerErrors = get(updateMcBannerAds, 'validationResult.errors', []);
    const addContentPromotionBannerErrors = get(addContentPromotionAds, 'validationResult.errors', []);
    const updateContentPromotionBannerErrors = get(updateContentPromotionAds, 'validationResult.errors', []);
    const deleteErrors = get(deleteAds, 'validationResult.errors', []);

    return {
        updated: {
            banners: (updateAds && updateAds.updatedAds) || [],
            errors: updateErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.update)
        },
        added: {
            banners: (addAds && addAds.addedAds) || [],
            errors: addErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.add)
        },
        smartAdded: {
            banners: (addSmartAds && addSmartAds.addedAds) || [],
            errors: addSmartErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.addSmartAds)
        },
        smartUpdated: {
            banners: (updateSmartAds && updateSmartAds.updatedAds) || [],
            errors: updateSmartErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.updateSmartAds)
        },
        cpmAdded: {
            banners: (addCpmAds && addCpmAds.addedAds) || [],
            errors: addCpmErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.addCpmAds)
        },
        cpmUpdated: {
            banners: (updateCpmAds && updateCpmAds.updatedAds) || [],
            errors: updateCpmErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.updateCpmAds)
        },
        mcBannerAdded: {
            banners: (addMcBannerAds && addMcBannerAds.addedAds) || [],
            errors: addMcBannerErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.addMcBannerAds)
        },
        mcBannerUpdated: {
            banners: (updateMcBannerAds && updateMcBannerAds.updatedAds) || [],
            errors: updateMcBannerErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.updateMcBannerAds)
        },
        contentPromotionBannersAdded: {
            banners: get(addContentPromotionAds, 'addedAds', []),
            errors: addContentPromotionBannerErrors.map((error: ValidationError) =>
                pick(error, ['code', 'params', 'path'])
            ),
            ids: Object.keys(input.addContentPromotionAds)
        },
        contentPromotionBannersUpdated: {
            banners: get(updateContentPromotionAds, 'updatedAds'),
            errors: updateContentPromotionBannerErrors.map((error: ValidationError) =>
                pick(error, ['code', 'params', 'path'])
            ),
            ids: Object.keys(input.updateContentPromotionAds)
        },
        deleted: {
            errors: deleteErrors.map((error: ValidationError) => pick(error, ['code', 'params', 'path'])),
            ids: Object.keys(input.delete)
        }
    };
};

export function getTurbolandingFromApiFormat(row: adsRowFragmentForEdit): EditedBannerTurbolanding | undefined {
    if (row.turbolanding) {
        const hrefParams =
            row.type === GdAdType.CPM_GEOPRODUCT ? undefined : getValueFromApiFormat(row.turbolanding.hrefParams);

        return {
            page: {
                landingId: String(row.turbolanding.id),
                name: row.turbolanding.name,
                href: row.turbolanding.href
            },
            hrefParams
        };
    } else {
        return undefined;
    }
}

/**
 * Преобразовываем пришедшие станции метро с бэка к нужному формату.
 * Проблема - на бэке Москва имеет id === 1 (метро), а с карт приходит Москва id === 213
 * @param stationsRowSet
 */
export const mapMetroStationsToQueryResponse = (
    stationsRowSet: ApolloQueryResult<MetroStationsQuery>
): Record<number, Array<MetroStationInfo>> => {
    return mapMetroStationsFromApi(stationsRowSet.data.constants.metroStations);
};

export const mapMetroStationsFromApi = (
    metroStations: MetroStationsQuery_constants_metroStations
): Record<number, Array<MetroStationInfo>> => {
    const stations = metroStations.rowset;

    return uniq(stations.map(station => station.geoRegionId)).reduce((acc, geoId) => {
        const key = geoId === 1 ? 213 : geoId; // подменяем id Москвы на 213, чтобы было проще доставать данные по метро
        return {
            ...acc,
            [key]: stations
                .filter(station => station.geoRegionId === geoId)
                .map(station => {
                    return {
                        val: station.metroStationId.toString(),
                        text: station.metroStationName
                    };
                })
        };
    }, {});
};

export const mapVCardsForEditQueryResultToQueryResponse = (
    result: ApolloQueryResult<VCardsForBannersEdit>
): { data: Record<BannerId, VcardData> } => {
    return {
        data: result.data.vcards.rowset.reduce((acc: Record<string, VcardData>, vCard) => {
            return {
                ...acc,
                [vCard.id]: mapVCardForEditQueryResultToQueryResponse(vCard)
            };
        }, {})
    };
};

export const mapVCardForEditQueryResultToQueryResponse = (
    vCard:
        | VCardsForBannersEdit_vcards_rowset
        | CampaignsEditDataQuery_client_campaigns_rowset_GdTextCampaign_commonVcard
        | BaseInfoForBannerCreationQuery_client_campaigns_rowset_GdTextCampaign_commonVcard
): VcardData => {
    return {
        campaignId: String(vCard.campaignId),
        name: vCard.companyName || '',
        ogrn: vCard.ogrn || '',
        phone: {
            codeCountry: vCard.phone.countryCode || '',
            codeRegion: vCard.phone.cityCode || '',
            phoneNumber: vCard.phone.phoneNumber || '',
            extension: vCard.phone.extension || ''
        },
        geoId: vCard.address.geoId,
        mail: vCard.email || '',
        office: vCard.address.apartment || '',
        descr: vCard.extraMessage || '',
        suggest: {},
        address: {
            country: getValueFromApiFormat(vCard.address.country),
            city: getValueFromApiFormat(vCard.address.city),
            street: getValueFromApiFormat(vCard.address.street),
            house: getValueFromApiFormat(vCard.address.houseWithBuilding)
        },
        messengerType: getMessengerTypeFromApi(vCard.instantMessenger && vCard.instantMessenger.type),
        messengerPhone: (vCard.instantMessenger && vCard.instantMessenger.login) || '',
        pointOnMap:
            vCard.address.pointOnMap !== null
                ? {
                      x: Number(vCard.address.pointOnMap.y),
                      y: Number(vCard.address.pointOnMap.x),
                      x1: Number(vCard.address.pointOnMap.y1),
                      y1: Number(vCard.address.pointOnMap.x1),
                      x2: Number(vCard.address.pointOnMap.y2),
                      y2: Number(vCard.address.pointOnMap.x2)
                  }
                : null,
        periods: vCard.workTimes.map((period: VCardsForBannersEdit_vcards_rowset_workTimes) => {
            return {
                week: mapVisitingCardPeriodDaysOfWeekFromApi(period.daysOfWeek),
                time: {
                    start: getTimeFromApi(period.startTime.hour, period.startTime.minute),
                    end: getTimeFromApi(period.endTime.hour, period.endTime.minute, true)
                }
            };
        }),
        metroStationId: vCard.address.metroStationId ? vCard.address.metroStationId.toString() : '0',
        metroStationName: vCard.address.metroStationName,
        contactPerson: vCard.contactPerson
    };
};

export const mapVisitingCardPeriodDaysOfWeekFromApi = (daysOfWeek: number[]): WeekInfo => {
    return daysOfWeek.map(dayIndex => {
        if (dayIndex < 0 || dayIndex > 6) {
            throw new Error(`Неверный формат графика работы визитки, dayIndex = ${dayIndex}`);
        }

        return dayIndex as Day;
    });
};

export const mapImagesForEditQueryResultToQueryResponse = (
    result: ApolloQueryResult<ImagesQueryForEdit>,
    type: BannerImageType
): Array<ImageItem> => {
    const { images } = result.data;

    return images.rowset.map((image: ImagesQueryForEdit_images_rowset) => {
        return {
            url: getUrlFromImageItem(image.mdsGroupId, image.namespace, image.imageHash),
            avatarsHost: image.avatarsHost,
            height: image.imageSize.height,
            width: image.imageSize.width,
            hash: image.imageHash,
            mdsGroupId: image.mdsGroupId,
            name: image.name,
            namespace: image.namespace,
            type,
            formats: image.formats ? mapAdImageFormatsFragmentToBannerImageFormat(image.formats) : []
        } as ImageItem;
    });
};

export const mapPreviewVideoApiData = (data: BannerVideoCreativeApiData): BannerVideoData => {
    return {
        livePreviewUrl: data.live_preview_url,
        id: data.stock_creative_id,
        name: data.name,
        previewUrl: data.resource_url,
        formats:
            data.additional_data &&
            data.additional_data.formats &&
            data.additional_data.formats.map(({ width, height }) => ({
                width,
                height
            })),
        duration: (data.additional_data && data.additional_data.duration) || data.duration
    };
};

const getBannersBaseConstants = (adConstants: adConstantsFragment): BannersBaseConstants => {
    return pick(adConstants, [
        'maxLengthTitle',
        'maxLengthTitleWord',
        'maxLengthTitleExtension',
        'maxLengthBody',
        'maxLengthBodyWord',
        'narrowSymbols',
        'maxLengthHref',
        'maxLengthTurboGalleryHref',
        'maxLengthDisplayHref',
        'allowAdLetters',
        'maxNumberOfNarrowCharacters',
        'aggregationDomains'
    ]);
};

const getVCardConstants = (vcardConstants: vcardConstantsFragment): VCardConstants => {
    return pick(vcardConstants, [
        'companyNameMaxLength',
        'contactPersonMaxLength',
        'contactEmailMaxLength',
        'extraMessageMaxLength',
        'countryMaxLength',
        'cityMaxLength',
        'streetMaxLength',
        'houseWithBuildingMaxLength',
        'apartmentMaxLength',
        'instantMessengerLoginMaxLength',
        'countryCodeMaxLength',
        'cityCodeMaxLength',
        'phoneNumberMinLength',
        'phoneNumberMaxLength',
        'extensionMaxLength',
        'entirePhoneMinLength',
        'entirePhoneMaxLength',
        'entirePhoneWithExtensionMaxLength'
    ]);
};

const getCalloutsConstants = (calloutsConstants: calloutsConstantsFragment): CalloutsConstants => {
    const {
        calloutLettersAllowed,
        calloutsOnClientMaxCount,
        calloutsOnClientMaxCountWithDeleted,
        calloutTextMaxLength
    } = calloutsConstants;
    return {
        calloutLettersAllowed,
        calloutTextMaxLength,
        calloutsOnClientMaxCount,
        calloutsOnClientMaxCountWithDeleted,
        calloutsOnBannerMaxNumber: 50,
        calloutsStringMaxLength: 66,
        showCalloutsMaxNumber: 4
    };
};

const getSiteLinksConstants = (sitelinkConstants: sitelinkConstantsFragment): SitelinksConstants => {
    return {
        allowSitelinkLetters: sitelinkConstants.allowLetters,
        maxLengthSitelinkTitle: sitelinkConstants.maxTitleLength,
        maxLengthSitelinkHref: sitelinkConstants.maxHrefLength,
        maxLengthSitelinkDescr: sitelinkConstants.maxDescriptionLength,
        maxLengthSitelinksTitle: 66
    };
};

const getCanvasConstants = (configurationConstants: configurationFragment): CanvasConstants => {
    return {
        canvasDomain: UrlHelpers.getCanvasDomain(configurationConstants.canvasUiDomain),
        turbolandingUnifiedApiUrl: configurationConstants.turbolandingUnifiedApiUrl
    };
};

const getCallouts = (callouts: Array<BannersQueryForEdit_callouts | null>): (BannersQueryForEdit_callouts)[] => {
    return callouts.reduce(
        (calloutList: Array<BannersQueryForEdit_callouts>, callout: BannersQueryForEdit_callouts) => {
            if (callout) {
                calloutList.push({
                    ...callout,
                    text: callout.text,
                    id: callout.id.toString()
                });
            }

            return calloutList;
        },
        []
    );
};

export const mapApiResponseToBaseInfoForBannerCreationData = (
    result: ApolloQueryResult<BaseInfoForBannerCreationQuery>
): BannersEditBaseInfoData => {
    const {
        client,
        callouts,
        constants,
        operator: { features: operatorFeatures }
    } = result.data;
    const {
        features,
        info: { id: clientId, workCurrency },
        adGroups,
        campaigns
    } = client;
    const { rowset: groupsRows } = adGroups;
    const { rowset: campaignsRows } = campaigns;

    const group = groupsRows ? groupsRows[0] : undefined;
    const campaign = campaignsRows ? campaignsRows[0] : undefined;
    const vCardData = campaign ? getCampaignVCardData(campaign) : undefined;
    const defaultOrganization =
        features.isCampaignDefaultOrganizationsEnabled && campaign
            ? getCampaignDefaultOrganization(campaign)
            : undefined;

    const promocodes: PromocodesRestrictedDomains = {};

    if (campaign && campaign.promocodeRestrictedDomain) {
        promocodes[campaign.id] = campaign.promocodeRestrictedDomain || [];
    }

    let vCardId: string = '';

    if (vCardData) {
        vCardId = vCardData.id || '';
    } else if (campaign) {
        vCardId = getCampaignLastVCardsIds(campaign) || '';
    }

    return {
        group: group
            ? {
                  name: group.name,
                  id: group.id,
                  adsCount: group.adsCount,
                  status: group.status.primaryStatus,
                  canEdit: group.access.canEdit,
                  type: mapGroupRowTypeToGroupType(group.type)
              }
            : undefined,
        campaign: campaign
            ? {
                  type: mapCampaignRowTypeToCampaignType(campaign.type),
                  platform: mapCampaignRowPlatformToCampaignPlatform(campaign.strategy.platform),
                  id: campaign.id,
                  name: campaign.name,
                  vCardData: vCardData ? mapVCardForEditQueryResultToQueryResponse(vCardData) : undefined,
                  isOnlyTurboAvailable:
                      campaign.strategy.__typename === 'GdStrategyOptimizeConversions' &&
                      Boolean(campaign.strategy.payForConversion),
                  defaultOrganization,
                  hasBanners: campaign.hasAds,
                  status: campaign.status.primaryStatus
              }
            : undefined,
        constants: {
            ad: getBannersBaseConstants(constants.validation.adConstants),
            vcard: getVCardConstants(constants.validation.vcardConstants),
            callouts: getCalloutsConstants(constants.validation.calloutConstants),
            sitelinks: getSiteLinksConstants(constants.validation.sitelinkConstants),
            canvas: getCanvasConstants(constants.configuration)
        },
        clientId,
        callouts: getCallouts(callouts),
        clientInfo: {
            isBannerUpdateAllowed: operatorFeatures ? operatorFeatures.isBannerUpdateAllowed : false,
            isCpmBannerUpdateAllowed: operatorFeatures ? operatorFeatures.isCpmBannerUpdateAllowed : false,
            isTurboForSerpWizardAllowed: features.isTurboForSerpWizardAllowed,
            clientId,
            workCurrency,
            isBannerPricesOnGridEnabled: features.isBannerPricesOnGridEnabled,
            isTycoonOrganizationsEnabled: features.isTycoonOrganizationsEnabled,
            isShowAutoAssignedOrganizationsEnabled: features.isShowAutoAssignedOrganizationsEnabled
        },
        promocodes,
        vCardId,
        vCardIds: campaign
            ? {
                  [campaign.id]: vCardId
              }
            : {}
    };
};

export const mapApiResponseToBannersData = (
    result: ApolloQueryResult<BannersQueryForEdit>
): BannersAndImageEditData => {
    const {
        client,
        callouts,
        constants,
        operator: { features: operatorFeatures },
        placements
    } = result.data;

    const {
        features,
        info: { id: clientId, workCurrency },
        ads: bannersResult,
        campaigns: campaignsResult
    } = client;
    const { rowset: bannersRows } = bannersResult;
    const { rowset: campaignsRows } = campaignsResult;
    const bannersCountsMap = {};

    const data = bannersRows.reduce(
        (
            acc: Pick<
                BannersEditApiData,
                'banners' | 'turbolandings' | 'video' | 'vCardIds' | 'creative' | 'vCardsData'
            >,
            bannerRow: adsRowFragmentForEdit
        ) => {
            bannersCountsMap[bannerRow.adGroup.id] = bannerRow.adGroup.adsCount;

            const bannerId = String(bannerRow.id);
            const campaignId = String(bannerRow.adGroup.campaign.id);
            const vCardData = getCampaignTruncatedVCardData(bannerRow.adGroup.campaign);

            const placementsHash = placements
                ? placements.placements.reduce((hash, { id, operatorName }) => {
                      hash[id] = operatorName;
                      return hash;
                  }, {})
                : ({} as Record<string, string>);

            acc.banners[bannerId] = makeEditedBanner(bannerRow, workCurrency, placementsHash);

            acc.video[bannerId] = getEditedBannerVideo(bannerRow);
            acc.vCardIds[campaignId] = getCampaignTruncatedLastVCardsIds(bannerRow.adGroup.campaign);

            acc.creative[bannerId] = getEditedBannerCreative(bannerRow);

            if (vCardData) {
                acc.vCardsData[campaignId] = {
                    vCardData: mapVCardForEditQueryResultToQueryResponse(vCardData),
                    vCardId: vCardData.id || undefined
                };
            }

            const turbolanding = getTurbolandingFromApiFormat(bannerRow);

            if (turbolanding) {
                acc.turbolandings[bannerId] = turbolanding;
            }

            return acc;
        },
        {
            banners: {},
            video: {},
            turbolandings: {},
            vCardIds: {},
            creative: {},
            vCardsData: {}
        }
    );

    const { banners, video, turbolandings, vCardIds, creative } = data;

    const promocodes: PromocodesRestrictedDomains = campaignsRows.reduce(
        (
            acc: PromocodesRestrictedDomains,
            campaign: BannersQueryForEdit_client_campaigns_rowset
        ): PromocodesRestrictedDomains => {
            const promocodesRestrictedDomain = campaign.promocodeRestrictedDomain || [];

            Object.keys(banners).map((bannerId: string) => {
                const banner: EditedBanner = banners[bannerId];

                if (banner.campaign.id === campaign.id.toString() && promocodesRestrictedDomain.length) {
                    const contacts: EditedBannerFullContacts = get(banner, 'contacts');

                    if (contacts) {
                        banner.isPromocodeActive = promocodesRestrictedDomain.some(
                            promocode => contacts.href.split(/\/|\?|\#/)[0].toLowerCase() === promocode
                        );
                    }
                }
            });

            return promocodesRestrictedDomain.length ? { ...acc, [campaign.id]: promocodesRestrictedDomain } : acc;
        },
        {}
    );

    const imageEditData = bannersRows.reduce(
        (acc: Record<string, ImageEditorState>, bannerRow: adsRowFragmentForEdit): Record<string, ImageEditorState> => {
            const bannerType = mapStringToAdvType(bannerRow.type);

            switch (bannerType) {
                case AdvType.TEXT:
                case AdvType.IMAGE_AD:
                case AdvType.CPM_VIDEO:
                case AdvType.CPM_AUDIO:
                case AdvType.CPC_VIDEO:
                case AdvType.MCBANNER:
                case AdvType.CPM_BANNER: {
                    acc[String(bannerRow.id)] = makeEditedImage(bannerRow);

                    return acc;
                }
                default:
                    return acc;
            }
        },
        {}
    );

    const defaultOrganizations: Record<CampaignId, string | undefined> = features.isCampaignDefaultOrganizationsEnabled
        ? campaignsRows.reduce(
              (
                  acc: Record<CampaignId, string | undefined>,
                  campaign: BannersQueryForEdit_client_campaigns_rowset
              ): Record<CampaignId, string | undefined> => {
                  return {
                      ...acc,
                      [campaign.id]: getCampaignDefaultOrganization(campaign)
                  };
              },
              {}
          )
        : {};

    return {
        bannersEditData: {
            turbolandings,
            banners,
            groupBannersList: GroupsHelpers.getGroupBannersList<GroupBanners>(banners, bannersCountsMap),
            constants: {
                ad: getBannersBaseConstants(constants.validation.adConstants),
                vcard: getVCardConstants(constants.validation.vcardConstants),
                callouts: getCalloutsConstants(constants.validation.calloutConstants),
                sitelinks: getSiteLinksConstants(constants.validation.sitelinkConstants),
                canvas: getCanvasConstants(constants.configuration)
            },
            callouts: getCallouts(callouts),
            clientId,
            clientInfo: {
                isBannerUpdateAllowed: operatorFeatures ? operatorFeatures.isBannerUpdateAllowed : false,
                isCpmBannerUpdateAllowed: operatorFeatures ? operatorFeatures.isCpmBannerUpdateAllowed : false,
                clientId,
                workCurrency,
                isBannerPricesOnGridEnabled: features.isBannerPricesOnGridEnabled,
                isTurboForSerpWizardAllowed: features.isTurboForSerpWizardAllowed,
                isTycoonOrganizationsEnabled: features.isTycoonOrganizationsEnabled,
                isShowAutoAssignedOrganizationsEnabled: features.isShowAutoAssignedOrganizationsEnabled
            },
            video,
            creative,
            promocodes,
            vCardIds,
            vCardsData: data.vCardsData,
            defaultOrganizations
        },
        imageEditData,
        reqId: result.data.reqId
    };
};

export const mapApiResponseToBannersDataForCopyPrevEditedBanners = (
    result: ApolloQueryResult<GetLastChangedAds>,
    group: EditedBannerGroup,
    measurerAccounts: MeasurerSystem[]
): BannersAndImageEditDataCopy => {
    const {
        operator: { features: operatorFeatures },
        client: {
            features,
            lastChangedAds,
            info: { id: clientId, workCurrency }
        },
        placements
    } = result.data;

    let bannersRows: GetLastChangedAds_client_lastChangedAds_lastChangedAds[] = [];

    if (lastChangedAds !== null) {
        if (lastChangedAds.lastChangedAds !== null) {
            bannersRows = lastChangedAds.lastChangedAds;
        }
    }

    const placementsHash = placements
        ? (placements.placements.reduce((hash, { id, operatorName }) => {
              hash[id] = operatorName;
              return hash;
          }, {}) as Record<string, string>)
        : ({} as Record<string, string>);

    const data = bannersRows.reduce(
        (
            acc: Pick<BannersEditApiData, 'banners' | 'turbolandings' | 'video' | 'vCardIds' | 'creative'>,
            bannerRow: GetLastChangedAds_client_lastChangedAds_lastChangedAds
        ) => {
            const bannerType = mapStringToAdvType(bannerRow.ad.type);
            const prevEditedBannerId = `${PrevEditedBanner}${bannerType}`;
            const campaignId = String(bannerRow.ad.adGroup.campaign.id);

            const bannerVideo = getEditedBannerVideo(bannerRow.ad);
            const bannerCreative = getEditedBannerCreative(bannerRow.ad);
            const turbolanding = getTurbolandingFromApiFormat(bannerRow.ad);

            // создаем баннеры двух типов TEXT и TEXT_MOBILE
            if (bannerType === AdvType.TEXT) {
                const prevEditedMobileBannerId = `${PrevEditedBanner}${AdvTypeClient.TEXT_MOBILE}`;

                bannerRow.ad.isMobile = true;

                acc.banners[prevEditedMobileBannerId] = makeEditedBanner(bannerRow.ad, workCurrency, placementsHash);

                bannerRow.ad.isMobile = false;

                acc.banners[prevEditedMobileBannerId].group = group;

                acc.video[prevEditedMobileBannerId] = bannerVideo;
                acc.creative[prevEditedMobileBannerId] = bannerCreative;

                if (turbolanding) {
                    acc.turbolandings[prevEditedMobileBannerId] = turbolanding;
                }
            }

            acc.banners[prevEditedBannerId] = makeEditedBanner(bannerRow.ad, workCurrency, placementsHash);

            acc.banners[prevEditedBannerId].group = group;

            acc.vCardIds[campaignId] = getCampaignTruncatedLastVCardsIds(bannerRow.ad.adGroup.campaign);

            acc.video[prevEditedBannerId] = bannerVideo;
            acc.creative[prevEditedBannerId] = bannerCreative;

            if (turbolanding) {
                acc.turbolandings[prevEditedBannerId] = turbolanding;
            }

            return acc;
        },
        {
            banners: {},
            video: {},
            turbolandings: {},
            vCardIds: {},
            creative: {}
        }
    );

    const { banners, video, turbolandings, vCardIds, creative } = data;

    const imageEditData = bannersRows.reduce(
        (
            acc: Record<string, ImageEditorState>,
            bannerRow: GetLastChangedAds_client_lastChangedAds_lastChangedAds
        ): Record<string, ImageEditorState> => {
            const bannerType = mapStringToAdvType(bannerRow.ad.type);
            const prevEditedBannerId = `${PrevEditedBanner}${bannerType}`;

            switch (bannerType) {
                case AdvType.TEXT: {
                    const prevEditedMobileBannerId = `${PrevEditedBanner}${AdvTypeClient.TEXT_MOBILE}`;
                    const editedImage = makeEditedImage(bannerRow.ad);

                    // создаем баннеры двух типов TEXT и TEXT_MOBILE
                    acc[prevEditedBannerId] = editedImage;
                    acc[prevEditedMobileBannerId] = editedImage;

                    return acc;
                }
                case AdvType.IMAGE_AD:
                case AdvType.CPM_VIDEO:
                case AdvType.CPM_AUDIO:
                case AdvType.CPC_VIDEO:
                case AdvType.MCBANNER:
                case AdvType.CPM_BANNER: {
                    acc[prevEditedBannerId] = makeEditedImage(bannerRow.ad);

                    return acc;
                }
                default:
                    return acc;
            }
        },
        {}
    );

    return {
        bannersEditData: {
            turbolandings,
            banners,
            video,
            creative,
            vCardIds,
            clientInfo: {
                isBannerUpdateAllowed: operatorFeatures ? operatorFeatures.isBannerUpdateAllowed : false,
                isCpmBannerUpdateAllowed: operatorFeatures ? operatorFeatures.isCpmBannerUpdateAllowed : false,
                clientId,
                workCurrency,
                isBannerPricesOnGridEnabled: features.isBannerPricesOnGridEnabled,
                isTurboForSerpWizardAllowed: features.isTurboForSerpWizardAllowed,
                isTycoonOrganizationsEnabled: features.isTycoonOrganizationsEnabled,
                isShowAutoAssignedOrganizationsEnabled: features.isShowAutoAssignedOrganizationsEnabled
            }
        },
        imageEditData,
        reqId: result.data.reqId
    };
};

export const mapCreativeForEditQueryResultToQueryResponse = (
    result: ApolloQueryResult<CreativesQuery>
): Array<BannerImageForEdit> => {
    const { creatives } = result.data;

    return creatives.rowset.map(
        (creative: CreativesQuery_creatives_rowset) =>
            ({
                name: creative.name,
                url: creative.previewUrl,
                width: creative.width ? parseFloat(creative.width) : 0,
                height: creative.height ? parseFloat(creative.height) : 0,
                id: creative.creativeId,
                type: BannerImageType.CREATIVE,
                creativeType: creative.creativeType,
                isImage: 'isImage' in creative ? creative.isImage : false,
                isAdaptive: creative.__typename === 'GdCanvasCreative' ? Boolean(creative.isAdaptive) : false
            } as BannerImageForEdit)
    );
};

interface CreativesData {
    creativeIds: string[];
    creatives: PerformanceCreatives;
    creativeGroups: PerformanceCreativeGroups;
}

type RowsetCreatives = PerformanceCreativesQuery_client_adGroups_rowset_GdSmartAdGroup_availableCreatives_rowset;

export const mapAdsGroupEditRecommendationsToQueryResponse = (
    response: ApolloQueryResult<RecommendationsAdsGroupEditQuery>
): Record<string, EditPageRecommendationsData> => {
    const adGroupsRowset = response.data.client.adGroups.rowset;

    return adGroupsRowset.reduce(
        (
            result: Record<string, EditPageRecommendationsData>,
            row: RecommendationsAdsGroupEditQuery_client_adGroups_rowset
        ) => {
            const { recommendations, id, type } = row;

            if (recommendations && recommendations.length) {
                result[id] = {
                    recommendations: recommendations
                        .filter(recommendation => isRecommendationSupportedOnEditBannerPage(recommendation.type, type))
                        .map(mapBaseRecommendationInfoFromApiFormat),
                    isRecommendationsVisible: true
                };
            }

            return result;
        },
        {}
    );
};

export const mapPerformanceCreatives = (
    response: ApolloQueryResult<PerformanceCreativesQuery>,
    banners: Record<BannerId, EditedBanner>,
    modalGroupId: string
): MapPerformanceCreatives => {
    const rowsetGroup = response.data.client.adGroups.rowset[0];

    if (rowsetGroup.__typename !== 'GdSmartAdGroup') {
        throw new Error(`Не верный тип данных: "${rowsetGroup.__typename}"`);
    }

    // формируем список всех креативов используемых в выбранной группе объявлений
    const selectedGroupCreatives: PerformanceSelectedGroupCreatives = response.data.client.ads.rowset.reduce(
        (
            groupsMap: PerformanceSelectedGroupCreatives,
            banner: PerformanceCreativesQuery_client_ads_rowset_GdSmartAd
        ) => {
            if (banner && banner.creative) {
                groupsMap[banner.creative.creativeId] = true;
            }

            return groupsMap;
        },
        {}
    );

    // из списка креативов группы убираем креативы, которые можно выбрать повторно
    // и помечаем креатив на который заменяем как используемый
    Object.keys(banners).forEach((bannerId: string): void => {
        const banner = banners[bannerId];
        if (banner.bannerType === AdvType.PERFORMANCE && modalGroupId === banner.group.id) {
            if (banner.creativeReplaced && banner.serverCreativeId) {
                // убираем заменяемый креатив из списка используемых креативов, для возможности повторного выбора
                delete selectedGroupCreatives[banner.serverCreativeId];

                // новый креатив помечаем как используемый, для исключения такого креатива из списка доступных
                selectedGroupCreatives[banner.creative.id] = true;
            }

            // креативы в новых объявлениям помечаем как используемые
            if (banner.isNew) {
                selectedGroupCreatives[banner.creative.id] = true;
            }
        }
    });

    // отфильтровываем nullable креативы
    const rowsetCreatives: RowsetCreatives[] =
        rowsetGroup.availableCreatives && rowsetGroup.availableCreatives.rowset
            ? rowsetGroup.availableCreatives.rowset.reduce((acc: RowsetCreatives[], elem: RowsetCreatives) => {
                  if (elem !== null) {
                      acc.push(elem);
                  }

                  return acc;
              }, [])
            : [];

    const { creativeIds, creatives, creativeGroups } = rowsetCreatives.reduce(
        (prev: CreativesData, creative: RowsetCreatives): CreativesData => {
            const id = String(creative.creativeId);

            // все креативы без групп будем помещать в группу с несуществующим id === '0'
            const groupId = creative.groupId ? creative.groupId : SPECIAL_CREATIVE_GROUP_ID_FOR_GROUP_WITHOUT_ID;
            // если у креатива id группы === '0', то явно указываем, что groupName === undefined
            const groupName =
                groupId !== SPECIAL_CREATIVE_GROUP_ID_FOR_GROUP_WITHOUT_ID
                    ? getValueFromApiFormat(creative.groupName)
                    : undefined;

            prev.creativeIds.push(id);

            prev.creatives[id] = {
                id,
                groupId,
                groupName,
                name: creative.name,
                height: creative.height ? parseFloat(creative.height) : 0,
                width: creative.width ? parseFloat(creative.width) : 0,
                businessType: creative.businessType,
                layout: creative.layout ? getValueFromApiFormat(creative.layout.name) : undefined,
                theme: creative.theme ? getValueFromApiFormat(creative.theme.name) : undefined,
                url: creative.previewUrl,
                statusModerate: creative.statusModerate,
                usedInCampaigns: creative.usedInCampaigns.map(campaign => {
                    return {
                        id: String(campaign.id),
                        name: campaign.name
                    };
                }),
                regionIds: creative.regionIds.map(String)
            };

            if (!prev.creativeGroups[groupId]) {
                prev.creativeGroups[groupId] = {
                    id: groupId,
                    name: getValueFromApiFormat(creative.groupName),
                    creativeIds: [id]
                };
            } else {
                prev.creativeGroups[groupId].creativeIds.push(id);
            }

            return prev;
        },
        {
            creativeIds: [],
            creatives: {},
            creativeGroups: {}
        }
    );

    const creativesAndGroupsList: CreativesAndGroupsList = creativeIds.reduce(
        (acc: CreativesAndGroupsList, creativeId) => {
            if (selectedGroupCreatives[creativeId]) {
                return acc;
            }

            const creative = creatives[creativeId];

            // для первого креатива вставляем данные по группе
            if (acc.length === 0) {
                acc.push(
                    {
                        type: CreativeAndGroupsTypeEnum.GROUP,
                        id: creative.groupId
                    },
                    {
                        type: CreativeAndGroupsTypeEnum.CREATIVE,
                        id: creative.id
                    }
                );

                return acc;
            }

            const lastCreativeId = acc[acc.length - 1].id;

            if (!lastCreativeId) {
                // последний элемент списка не может быть элементом группы, поэтому id обязан быть всегда
                throw new Error('Последний элемент списка не имеет id');
            }

            const lastCreative = creatives[lastCreativeId];

            // если у предыдущего креатива отличается groupId то добавляем данные по группе
            if (creative.groupId !== lastCreative.groupId) {
                acc.push(
                    {
                        type: CreativeAndGroupsTypeEnum.GROUP,
                        id: creative.groupId
                    },
                    {
                        type: CreativeAndGroupsTypeEnum.CREATIVE,
                        id: creative.id
                    }
                );

                // если у предыдущего креатива такой же groupId, то просто добавляем креатив
            } else {
                acc.push({
                    type: CreativeAndGroupsTypeEnum.CREATIVE,
                    id: creative.id
                });
            }

            return acc;
        },
        []
    );

    return {
        creativesAndGroupsList,
        selectedGroupCreatives,
        creativeGroups,
        creatives,
        creativeIds,
        creativesLoadedCount: rowsetCreatives.length
    };
};

export function getBannerPriceDataEditor(
    bannerRow: adsRowFragmentForEdit_GdTextAd,
    workCurrency: CurrencyCode
): EditedBannerPrice {
    const bannerPrice = bannerRow.bannerPrice;

    return {
        isOpen: false,
        isOnProgress: false,
        bannerPrice: bannerPrice
            ? getBannerPriceData(bannerPrice, workCurrency)
            : getDefaultBannerPriceData(workCurrency)
    };
}

export function makeAudienceCollectionFromApi(goal: GdVideoGoalType): AudienceCollection {
    switch (goal) {
        case GdVideoGoalType.COMPLETE:
            return AudienceCollection.COMPLETE;
        case GdVideoGoalType.FIRST_QUARTILE:
            return AudienceCollection.FIRST_QUARTILE;
        case GdVideoGoalType.MIDPOINT:
            return AudienceCollection.MIDPOINT;
        case GdVideoGoalType.THIRD_QUARTILE:
            return AudienceCollection.THIRD_QUARTILE;
        case GdVideoGoalType.START:
            return AudienceCollection.START;
        default:
            throw new Error(`данный тип не поддерживается!`);
    }
}

export function makeAudienceCollectionForApi(goal: AudienceCollection): GdVideoGoalTypeInput {
    switch (goal) {
        case AudienceCollection.COMPLETE:
            return GdVideoGoalTypeInput.COMPLETE;
        case AudienceCollection.FIRST_QUARTILE:
            return GdVideoGoalTypeInput.FIRST_QUARTILE;
        case AudienceCollection.MIDPOINT:
            return GdVideoGoalTypeInput.MIDPOINT;
        case AudienceCollection.THIRD_QUARTILE:
            return GdVideoGoalTypeInput.THIRD_QUARTILE;
        case AudienceCollection.START:
            return GdVideoGoalTypeInput.START;
        default:
            throw new Error(`данный тип не поддерживается!`);
    }
}

function makeEditedBanner(
    bannerRow: adsRowFragmentForEdit,
    workCurrency: CurrencyCode,
    placements: Record<string, string>
): EditedBanner {
    const bannerType = mapStringToAdvType(bannerRow.type);

    switch (bannerType) {
        case AdvType.TEXT:
            return makeEditedBannerTypeText(bannerRow, workCurrency);
        case AdvType.IMAGE_AD:
            return makeEditedBannerTypeImage(bannerRow);
        case AdvType.CPC_VIDEO:
            return makeEditedBannerTypeCpcVideo(bannerRow);
        case AdvType.CPM_VIDEO:
            return makeEditedBannerTypeCpmVideo(bannerRow);
        case AdvType.CPM_AUDIO:
            return makeEditedBannerTypeCpmAudio(bannerRow);
        case AdvType.CPM_BANNER:
            return makeEditedBannerTypeCpmBanner(bannerRow);
        case AdvType.CPM_GEOPRODUCT:
            return makeEditedBannerTypeCpmGeoproduct(bannerRow);
        case AdvType.CPM_INDOOR:
            return makeEditedBannerTypeCpmIndoor(bannerRow);
        case AdvType.CPM_OUTDOOR:
            return makeEditedBannerTypeCpmOutdoor(bannerRow, placements);
        case AdvType.MCBANNER:
            return makeEditedBannerTypeMcBanner(bannerRow);
        case AdvType.PERFORMANCE:
            return makeEditedBannerTypePerformance(bannerRow);
        case AdvType.DYNAMIC:
            return makeEditedBannerTypeDynamic(bannerRow);
        case AdvType.MOBILE_CONTENT:
            return makeEditedBannerTypeMobileContent(bannerRow);
        case AdvType.CONTENT_PROMOTION_COLLECTION:
            return makeEditedBannerTypeContentPromotionCollection(bannerRow);
        case AdvType.CONTENT_PROMOTION_VIDEO:
            return makeEditedBannerTypeContentPromotionVideo(bannerRow);
        default:
            throw new Error(`banner type ${bannerType} is not supported!`);
    }
}

function makeEditedImage(bannerRow: adsRowFragmentForEdit): ImageEditorState {
    return {
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        image: getEditedBannerImage(bannerRow),
        imageUploadingLink: '',
        isCropOpen: false,
        isImageCanvasOpen: false
    };
}

function makeEditedBannerTypeImage(bannerRow: adsRowFragmentForEdit): EditedBannerTypeImage {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.IMAGE_AD,
        isImageCanvasOpen: false,
        creativeId: getValueFromApiFormat(bannerRow.typedCreative && bannerRow.typedCreative.creativeId),
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        }
    };
}

function makeEditedBannerTypeCpcVideo(bannerRow: adsRowFragmentForEdit): EditedBannerTypeCpcVideo {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.CPC_VIDEO,
        video: getEditedBannerVideo(bannerRow),
        creativeId: getValueFromApiFormat(bannerRow.typedCreative && bannerRow.typedCreative.creativeId),
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        }
    };
}

function makeEditedBannerTypeCpmAudio(bannerRow: adsRowFragmentForEdit): EditedBannerTypeCpmAudio {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const pixels = getPixelsFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const measurers = getMeasurersFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.CPM_AUDIO,
        video: getEditedBannerVideo(bannerRow),
        creativeId: getValueFromApiFormat(bannerRow.typedCreative && bannerRow.typedCreative.creativeId),
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        isImageCanvasOpen: false,
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            turbolanding: getTurbolandingFromApiFormat(bannerRow),
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        measurers,
        pixel: pixels.audience.length ? pixels.audience[0] : '',
        showsCounters: pixels.audit.length ? pixels.audit : [''],
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        }
    };
}

function makeEditedBannerTypeCpmVideo(bannerRow: adsRowFragmentForEdit): EditedBannerTypeCpmVideo {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const pixels = getPixelsFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const measurers = getMeasurersFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.CPM_VIDEO,
        creative: getEditedBannerCreative(bannerRow),
        creativeId: getValueFromApiFormat(bannerRow.typedCreative && bannerRow.typedCreative.creativeId),
        video: getEditedBannerVideo(bannerRow),
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        isImageCanvasOpen: false,
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            turbolanding: getTurbolandingFromApiFormat(bannerRow),
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        pixel: pixels.audience.length ? pixels.audience[0] : '',
        measurers,
        tnsId: get(bannerRow, 'tnsId', undefined),
        showsCounters: pixels.audit.length ? pixels.audit : [''],
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        }
    };
}

function makeEditedBannerTypeCpmOutdoor(
    bannerRow: adsRowFragmentForEdit,
    placements: Record<string, string>
): EditedBannerTypeCpmOutdoor {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const pixels = getPixelsFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const measurers = getMeasurersFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;
    const readOnly = bannerRow.status.primaryStatus !== GdAdPrimaryStatus.DRAFT;

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.CPM_OUTDOOR,
        creative: getEditedBannerCreative(bannerRow),
        readOnly,
        video: getEditedBannerVideo(bannerRow),
        creativeId: getValueFromApiFormat(bannerRow.typedCreative && bannerRow.typedCreative.creativeId),
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        operators:
            'placementPages' in bannerRow
                ? bannerRow.placementPages.reduce(
                      (acc: OperatorModerationInfo[], { pageId, statusModerate, statusModerateOperator }) => {
                          acc.push({
                              name: placements[pageId],
                              statusModerate,
                              statusModerateOperator
                          });
                          return acc;
                      },
                      [] as OperatorModerationInfo[]
                  )
                : [],
        isImageCanvasOpen: false,
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            turbolanding: getTurbolandingFromApiFormat(bannerRow),
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        pixel: pixels.audience.length ? pixels.audience[0] : '',
        measurers,
        showsCounters: pixels.audit.length ? pixels.audit : [''],
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        }
    };
}

function makeEditedBannerTypeCpmBanner(bannerRow: adsRowFragmentForEdit): EditedBannerTypeCpmBanner {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const pixels = getPixelsFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const measurers = getMeasurersFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.CPM_BANNER,
        creativeId: getValueFromApiFormat(bannerRow.typedCreative && bannerRow.typedCreative.creativeId),
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        isImageCanvasOpen: false,
        creative: getEditedBannerCreative(bannerRow),
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            turbolanding: getTurbolandingFromApiFormat(bannerRow),
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        pixel: pixels.audience.length ? pixels.audience[0] : '',
        measurers,
        showsCounters: pixels.audit.length ? pixels.audit : [''],
        tnsId: get(bannerRow, 'tnsId', undefined),
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        }
    };
}

function makeEditedBannerTypeCpmGeoproduct(bannerRow: adsRowFragmentForEdit): EditedBannerTypeCpmGeoproduct {
    const pixels = getPixelsFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const measurers = getMeasurersFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.CPM_GEOPRODUCT,
        creative: getEditedBannerCreative(bannerRow),
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        isImageCanvasOpen: false,
        contacts: {
            turbolanding: getTurbolandingFromApiFormat(bannerRow),
            mobileType: ContactsEditorType.TURBO
        },
        pixel: pixels.audience.length ? pixels.audience[0] : '',
        measurers,
        showsCounters: pixels.audit.length ? pixels.audit : [''],
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        }
    };
}

function makeEditedBannerTypeCpmIndoor(bannerRow: adsRowFragmentForEdit): EditedBannerTypeCpmIndoor {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const pixels = getPixelsFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const measurers = getMeasurersFromApiFormat(bannerRow as adsRowFragmentForEdit_GdCPMBannerAd);
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.CPM_INDOOR,
        creative: getEditedBannerCreative(bannerRow),
        video: getEditedBannerVideo(bannerRow),
        creativeId: getValueFromApiFormat(bannerRow.typedCreative && bannerRow.typedCreative.creativeId),
        measurers,
        imageGallery: {
            images: undefined,
            isImageGalleryOpen: false,
            selectedImageHashFromGallery: []
        },
        isImageCanvasOpen: false,
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            turbolanding: getTurbolandingFromApiFormat(bannerRow),
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        pixel: pixels.audience.length ? pixels.audience[0] : '',
        showsCounters: pixels.audit.length ? pixels.audit : [''],
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        }
    };
}

function makeEditedBannerTypeDynamic(bannerRow: adsRowFragmentForEdit): EditedBannerTypeDynamic {
    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.DYNAMIC
    };
}

function makeEditedBannerTypeMcBanner(bannerRow: adsRowFragmentForEdit): EditedBannerTypeMcBanner {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;

    return {
        ...makeEditedBannerBase(bannerRow),
        imageHash: getValueFromApiFormat(bannerRow.image && bannerRow.image.imageHash),
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            turbolanding: getTurbolandingFromApiFormat(bannerRow),
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        },
        bannerType: AdvType.MCBANNER
    };
}

function makeEditedBannerTypePerformance(bannerRow: adsRowFragmentForEdit): EditedBannerTypePerformance {
    const { typedCreative } = bannerRow;

    if (typedCreative && typedCreative.__typename !== 'GdSmartCreative') {
        throw new Error(`Не поддерживаемый тип креатива: ${typedCreative.__typename}`);
    }

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.PERFORMANCE,
        creative: {
            id: typedCreative ? typedCreative.creativeId : '',
            name: typedCreative && typedCreative.name ? getValueFromApiFormat(typedCreative.name) : undefined,
            url: typedCreative ? typedCreative.previewUrl : '',
            height: typedCreative ? Number(typedCreative.height) : 0,
            width: typedCreative ? Number(typedCreative.width) : 0,
            layout:
                typedCreative && typedCreative.layout ? getValueFromApiFormat(typedCreative.layout.name) : undefined,
            groupId: (typedCreative && typedCreative.groupId) || SPECIAL_CREATIVE_GROUP_ID_FOR_GROUP_WITHOUT_ID,
            groupName: typedCreative ? getValueFromApiFormat(typedCreative.groupName) : undefined
        },
        creativeReplaced: false
    };
}

function makeEditedBannerTypeMobileContent(bannerRow: adsRowFragmentForEdit): EditedBannerTypeMobileContent {
    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: AdvType.MOBILE_CONTENT
    };
}

function makeEditedBannerBase(bannerRow: adsRowFragmentForEdit): Omit<EditedBannerBase, 'bannerType'> {
    const strategy = bannerRow.adGroup.campaign.strategy;

    return {
        id: String(bannerRow.id),
        isNew: false,
        isSaved: false,
        canBeDeleted: Boolean(bannerRow.access.canBeDeleted),
        readOnly: false,
        markedForDeletion: false,
        isMobile: Boolean(bannerRow.isMobile),
        isEditAllow: bannerRow.access.canEdit && bannerRow.status.primaryStatus !== GdAdPrimaryStatus.ARCHIVED,

        group: {
            id: String(bannerRow.adGroup.id),
            name: bannerRow.adGroup.name,
            status: bannerRow.adGroup.status.primaryStatus,
            canEdit: bannerRow.adGroup.access.canEdit,
            type: mapGroupRowTypeToGroupType(bannerRow.adGroup.type)
        },
        campaign: {
            id: String(bannerRow.adGroup.campaign.id),
            name: bannerRow.adGroup.campaign.name,
            platform: bannerRow.adGroup.campaign.strategy.platform,
            type: mapCampaignRowTypeToCampaignType(bannerRow.adGroup.campaign.type),
            isOnlyTurboAvailable:
                strategy.__typename === 'GdStrategyOptimizeConversions' && Boolean(strategy.payForConversion),
            hasBanners: bannerRow.adGroup.campaign.hasAds,
            status: bannerRow.adGroup.campaign.status.primaryStatus
        },
        status: {
            type: getValueFromApiFormat(bannerRow.status.primaryStatus),
            isPreviousVersionShown: Boolean(bannerRow.status.previousVersionShown),
            isRejectedOnModeration: Boolean(bannerRow.status.rejectedOnModeration)
        }
    };
}

function getContactsType(
    bannerRow: adsRowFragmentForEdit
): {
    desktopAndTabletType: ContactsEditorType;
    mobileType: ContactsEditorType;
} {
    const turbolanding = getTurbolandingFromApiFormat(bannerRow);
    const link = bannerRow.href;
    const vcard = bannerRow.vcardId;
    const permalinkId = bannerRow.__typename === 'GdTextAd' && bannerRow.permalinkId;
    const strategy = bannerRow.adGroup.campaign.strategy;
    const isOnlyTurboAvailable = strategy.__typename === 'GdStrategyOptimizeConversions' && strategy.payForConversion;

    if (isOnlyTurboAvailable) {
        return {
            desktopAndTabletType: ContactsEditorType.TURBO,
            mobileType: ContactsEditorType.TURBO
        };
    }

    if (link && turbolanding) {
        return {
            desktopAndTabletType: ContactsEditorType.LINK,
            mobileType: ContactsEditorType.TURBO
        };
    }

    if (link) {
        return {
            desktopAndTabletType: ContactsEditorType.LINK,
            mobileType: ContactsEditorType.LINK
        };
    }

    if (turbolanding) {
        return {
            desktopAndTabletType: ContactsEditorType.TURBO,
            mobileType: ContactsEditorType.TURBO
        };
    }

    if (permalinkId) {
        return {
            desktopAndTabletType: ContactsEditorType.ORGANIZATION,
            mobileType: ContactsEditorType.ORGANIZATION
        };
    }

    if (vcard) {
        return {
            desktopAndTabletType: ContactsEditorType.VCARD,
            mobileType: ContactsEditorType.VCARD
        };
    }

    return {
        desktopAndTabletType: ContactsEditorType.LINK,
        mobileType: ContactsEditorType.LINK
    };
}

function makeEditedBannerTypeText(bannerRow: adsRowFragmentForEdit, workCurrency: CurrencyCode): EditedBannerTypeText {
    const href = getValueFromApiFormat(bannerRow.href) || '';
    const domain = getValueFromApiFormat(bannerRow.domain) || '';
    const hrefProtocol = UrlHelpers.extractProtocol(href) as ProtocolValue;

    return {
        ...makeEditedBannerBase(bannerRow),
        bannerType: bannerRow.isMobile ? AdvTypeClient.TEXT_MOBILE : AdvType.TEXT,

        title: getValueFromApiFormat(bannerRow.title) || '',
        titleExtension: getValueFromApiFormat(bannerRow.titleExtension) || '',
        body: getValueFromApiFormat(bannerRow.body) || '',
        contacts: {
            href: UrlHelpers.deleteProtocol(href),
            hrefProtocol: hrefProtocol || ProtocolValue.https,
            domain,
            displayHref: getValueFromApiFormat(bannerRow.linkTail) || '',
            isUrlChecking: false,
            ...getContactsType(bannerRow)
        },
        dynamicDisclaimer: getValueFromApiFormat(bannerRow.dynamicDisclaimer),
        callouts: getEditedBannerCallouts(bannerRow),
        turboGallery: getEditedBannerTurboGallery(bannerRow),
        imageHash: getValueFromApiFormat(bannerRow.bannerImage && bannerRow.bannerImage.imageHash),
        creativeId: getValueFromApiFormat(bannerRow.typedCreative && bannerRow.typedCreative.creativeId),
        sitelinksSetId: bannerRow.sitelinksSetId || null,
        sitelinks: getEditedBannerSiteLinks(bannerRow),
        linkTail: getValueFromApiFormat(bannerRow.linkTail),
        age: getAgeFromBannerModerationTheme((bannerRow.flags || []).filter(el => el) as BannerModerationTheme[]),
        warning: getWarningFromBannerModerationTheme((bannerRow.flags || []).filter(
            el => el
        ) as BannerModerationTheme[]),
        vCardId: bannerRow.vcardId || '',
        vCard: getEditedBannerVCard(bannerRow.vcardId, bannerRow.campaignId),
        bannerPriceEditor: getBannerPriceDataEditor(bannerRow as adsRowFragmentForEdit_GdTextAd, workCurrency),
        displayConditions: {
            copyTooltipStatus: CopyTooltipStatus.hidden,
            isOpen: false,
            isLoading: false,
            keywordsTextTypeView: KeywordsTextTypeView.horizontal
        },
        permalinkId: getBannerTypeTextPermalinkId(bannerRow),
        suggestedPermalinkId: getBannerTypeTextSuggestedPermalinkId(bannerRow)
    };
}

function makeEditedContentPromotionBannerBase(
    bannerRow: adsRowFragmentForEdit
): Omit<EditedBannerTypeContentPromotionBase, 'bannerType'> {
    if (bannerRow.__typename !== 'GdContentPromotionAd') {
        throw new Error(`banner with typename of ${bannerRow.__typename} is forbidden here`);
    }

    const visitUrl = getValueFromApiFormat(bannerRow.contentPromotionVisitUrl) || '';
    const protocol = (UrlHelpers.extractProtocol(visitUrl) as ProtocolValue) || ProtocolValue.https;

    return {
        ...makeEditedBannerBase(bannerRow),
        title: getValueFromApiFormat(bannerRow.title) || '',
        body: getValueFromApiFormat(bannerRow.body) || '',
        contentId: bannerRow.contentPromotionId,
        contentPromotionUrl: {
            value: getValueFromApiFormat(bannerRow.contentPromotionUrl) || '',
            isChecking: false
        },
        previewUrl: bannerRow.contentPromotionPreviewUrl || '',
        contacts: {
            href: UrlHelpers.deleteProtocol(visitUrl),
            hrefProtocol: protocol,
            isUrlChecking: false,

            // пока неиспользуемые, но заполненные по дефолту поля для CONTENT_PROMOTION
            domain: '',
            displayHref: '',
            desktopAndTabletType: ContactsEditorType.LINK,
            mobileType: ContactsEditorType.LINK
        }
    };
}

function makeEditedBannerTypeContentPromotionCollection(
    bannerRow: adsRowFragmentForEdit
): EditedBannerTypeContentPromotionCollection {
    return {
        ...makeEditedContentPromotionBannerBase(bannerRow),
        bannerType: AdvType.CONTENT_PROMOTION_COLLECTION
    };
}

function makeEditedBannerTypeContentPromotionVideo(
    bannerRow: adsRowFragmentForEdit
): EditedBannerTypeContentPromotionVideo {
    return {
        ...makeEditedContentPromotionBannerBase(bannerRow),
        bannerType: AdvType.CONTENT_PROMOTION_VIDEO
    };
}

export function getBannerTypeTextPermalinkId(bannerRow: adsRowFragmentForEdit): string | undefined {
    switch (bannerRow.__typename) {
        case 'GdTextAd':
            return !bannerRow.permalinkAssignType || bannerRow.permalinkAssignType === GdAdPermalinkAssignType.MANUAL
                ? bannerRow.permalinkId || undefined
                : undefined;
        default:
            return undefined;
    }
}

export function getBannerTypeTextSuggestedPermalinkId(bannerRow: adsRowFragmentForEdit): string | undefined {
    switch (bannerRow.__typename) {
        case 'GdTextAd':
            return bannerRow.permalinkAssignType && bannerRow.permalinkAssignType === GdAdPermalinkAssignType.AUTO
                ? bannerRow.permalinkId || undefined
                : undefined;
        default:
            return undefined;
    }
}

export function getCampaignTruncatedLastVCardsIds(
    campaign: adsRowFragmentForEdit_GdTextAd_adGroup_GdDynamicAdGroup_campaign
): string | undefined {
    switch (campaign.__typename) {
        case 'GdDynamicCampaign':
        case 'GdTextCampaign':
            return campaign.lastChangedVcardId === null ? undefined : campaign.lastChangedVcardId;
        default:
            return undefined;
    }
}

export function getCampaignLastVCardsIds(
    campaign: BaseInfoForBannerCreationQuery_client_campaigns_rowset
): string | undefined {
    switch (campaign.__typename) {
        case 'GdDynamicCampaign':
        case 'GdTextCampaign':
            return campaign.lastChangedVcardId === null ? undefined : campaign.lastChangedVcardId;
        default:
            return undefined;
    }
}

export function mapGeneratedVideoGroupApiFormat(
    generatedVideo: (GenerateVideoAddition_generateVideoAddition | null)[],
    groupsIds: string[]
): VideoState {
    return groupsIds.reduce((acc, next, index) => {
        const video = generatedVideo[index];

        if (video) {
            acc[next] = {
                data: {
                    id: video.creativeId,
                    name: video.name,
                    previewUrl: video.previewUrl
                }
            };
        }

        return acc;
    }, {});
}

export function mapVideoGroupsToBannerFormat(
    groupsVideoState: VideoState,
    groupsBannerList: Record<string, GroupBanners>,
    banners: Record<string, EditedBanner>
): VideoState {
    return Object.keys(groupsVideoState).reduce((acc: VideoState, videoGroupId) => {
        const { bannersIds } = groupsBannerList[videoGroupId];

        bannersIds.forEach(bannerId => {
            const banner = banners[bannerId];

            if (banner.isNew && banner.bannerType === AdvType.TEXT) {
                acc[bannerId] = groupsVideoState[videoGroupId];
            }
        });

        return acc;
    }, {});
}

export function mapGroupsDataToBannerVideoFormat(groupBannersList: Record<string, GroupBanners>): VideoState {
    return Object.keys(groupBannersList).reduce((acc, key) => {
        acc[key] = groupBannersList[key].autoGeneratedVideo;

        return acc;
    }, {});
}

export function getCampaignTruncatedVCardData(
    campaign: adsRowFragmentForEdit_GdTextAd_adGroup_GdDynamicAdGroup_campaign
): BaseInfoForBannerCreationQuery_client_campaigns_rowset_GdTextCampaign_commonVcard | undefined {
    switch (campaign.__typename) {
        case 'GdDynamicCampaign':
        case 'GdTextCampaign':
            return campaign.commonVcard === null ? undefined : campaign.commonVcard;
        default:
            return undefined;
    }
}

export function getCampaignVCardData(
    campaign: BaseInfoForBannerCreationQuery_client_campaigns_rowset
): BaseInfoForBannerCreationQuery_client_campaigns_rowset_GdTextCampaign_commonVcard | undefined {
    switch (campaign.__typename) {
        case 'GdDynamicCampaign':
        case 'GdTextCampaign':
            return campaign.commonVcard === null ? undefined : campaign.commonVcard;
        default:
            return undefined;
    }
}

export function getCampaignDefaultOrganization(
    campaign: BaseInfoForBannerCreationQuery_client_campaigns_rowset | BannersQueryForEdit_client_campaigns_rowset
): string | undefined {
    switch (campaign.__typename) {
        case 'GdTextCampaign':
            return campaign.defaultPermalinkId === null ? undefined : campaign.defaultPermalinkId;
        default:
            return undefined;
    }
}
